import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";
import * as groupRequestsService from "../services/groupRequestsService";
import * as groupsService from "../services/groupsService";

export function useRequestsQuery({ userId, isAdmin }) {
  return useQuery({
    queryKey: ["groupRequests", userId, isAdmin],
    queryFn: () => groupRequestsService.listRequests({ userId, isAdmin }),
    enabled: !!userId,
  });
}

export function useNotifySettingsQuery({ isAdmin }) {
  return useQuery({
    queryKey: ["notifySettings"],
    queryFn: groupRequestsService.getNotifySettings,
    enabled: !!isAdmin,
  });
}

export function useUnreadRequestCount({ userId, isAdmin }) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return undefined;
    const unsubscribe = groupRequestsService.subscribeToRequestChanges({ userId, isAdmin }, () => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests", "unreadCount", userId, isAdmin] });
    });
    return unsubscribe;
  }, [userId, isAdmin, queryClient]);

  return useQuery({
    queryKey: ["groupRequests", "unreadCount", userId, isAdmin],
    queryFn: () => groupRequestsService.getUnreadCount({ userId, isAdmin }),
    enabled: !!userId,
  });
}

export function useSubmitRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupRequestsService.submitRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests"] });
    },
  });
}

export function useRecordAdminAdd() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupRequestsService.recordAdminAdd,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests"] });
    },
  });
}

export function useRecordAdminRemove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupRequestsService.recordAdminRemove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests"] });
    },
  });
}

export function useSendAdminMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupRequestsService.sendAdminMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests"] });
    },
  });
}

export function useApproveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, requestType, fromUserId, groupId, targetUserId, newGroupName, invitedEmails, resolvedBy, displayName }) => {
      if (requestType === "join") {
        await groupsService.addMember({ groupId, userId: fromUserId, displayName });
      } else if (requestType === "create") {
        const newGroup = await groupsService.createGroup({ name: newGroupName, createdBy: resolvedBy });
        await groupsService.addMember({ groupId: newGroup.id, userId: fromUserId, displayName });
      } else if (requestType === "leave") {
        await groupsService.removeMember({ groupId, userId: fromUserId });
      } else if (requestType === "leader_change") {
        await groupsService.setLeader({ groupId, leaderId: targetUserId });
      } else if (requestType === "bulk_invite") {
        const { error } = await supabase.functions.invoke("send-group-invites", { body: { requestId } });
        if (error) {
          logger.error('useGroupRequests', 'bulk_invite_failed', { requestId, error: error.message });
        } else {
          logger.info('useGroupRequests', 'bulk_invite_sent', { requestId, emailCount: invitedEmails?.length || 0 });
        }
        return; // send-group-invites already wrote status/resolved_at/resolved_by/invite_results itself
      }

      await groupRequestsService.approveRequest({ requestId, resolvedBy, requestType, fromUserId });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["groupMemberships"] });
      queryClient.invalidateQueries({ queryKey: ["userMemberships", variables.fromUserId] });
    },
  });
}

export function useRejectRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupRequestsService.rejectRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests"] });
    },
  });
}

export function useReceiveFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupRequestsService.receiveFeedback,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests"] });
    },
  });
}

export function useAddFeedbackToTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupRequestsService.addFeedbackToTodo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests"] });
    },
  });
}

export function useAssignVacancyLeader() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, resolvedBy, groupId, targetUserId }) => {
      await groupsService.setLeader({ groupId, leaderId: targetUserId });
      await groupRequestsService.assignVacancyLeader({ requestId, resolvedBy, groupId, targetUserId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groupRequests"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useSetNotifyTarget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: groupRequestsService.setNotifySettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifySettings"] });
    },
  });
}
